import { useMemo, useState } from "react";
import styles from "../../ConversionAndPdpViewer.module.css";
import { resolveProductUrl } from "./formatters";
import { MetricCard } from "./ui";

function PdpBadge({ tone = "gray", children }) {
  return (
    <span className={`${styles.pdpTwinBadge} ${styles[`pdpTwinBadge${tone}`] || ""}`}>
      {children}
    </span>
  );
}

function statusClass(stylesRef, status = "") {
  const normalized = String(status).toLowerCase();
  if (normalized === "healthy" || normalized === "completed") return stylesRef.preAnalysisStatusHealthy;
  if (normalized === "critical") return stylesRef.preAnalysisStatusCritical;
  if (normalized === "attention" || normalized === "needs attention") return stylesRef.preAnalysisStatusAttention;
  return "";
}

function renderCell(cell, index) {
  if (cell && typeof cell === "object" && !Array.isArray(cell)) {
    return (
      <span>
        {cell.text || "-"}
        {cell.href ? (
          <a className={styles.pdpTwinInlineLink} href={cell.href} target="_blank" rel="noopener noreferrer">
            {cell.linkLabel || "View"}
          </a>
        ) : null}
      </span>
    );
  }
  return cell ?? (index === 0 ? "Untitled" : "-");
}

function PdpDataTable({ table = null }) {
  const headers = Array.isArray(table?.headers) ? table.headers : [];
  const rows = Array.isArray(table?.rows) ? table.rows : [];
  if (!headers.length || !rows.length) return null;

  return (
    <div className={styles.pdpTwinTableWrap}>
      <table className={styles.pdpTwinTable}>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const cells = Array.isArray(row?.cells) ? row.cells : [];
            return (
              <tr key={row.key || rowIndex}>
                {cells.map((cell, cellIndex) => (
                  <td key={`${rowIndex}-${cellIndex}`}>{renderCell(cell, cellIndex)}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PdpJourneyFunnel({ stages = [] }) {
  const max = Math.max(...stages.map((stage) => Number(stage.value || 0)), 1);
  return (
    <div className={styles.pdpJourneyFunnel}>
      {stages.map((stage) => {
        const value = Number(stage.value || 0);
        const width = Math.max(value > 0 ? (value / max) * 100 : 4, 4);
        const pct = max > 0 ? value / max : 0;
        return (
          <div key={stage.label} className={styles.pdpJourneyStage}>
            <div>
              <div className={styles.pdpJourneyStageName}>{stage.label}</div>
              <div className={styles.pdpJourneyStageNote}>{stage.note}</div>
            </div>
            <div className={styles.pdpJourneyRail}>
              <div
                className={`${styles.pdpJourneyFill} ${value === 0 ? styles.pdpJourneyFillRed : ""}`}
                style={{ width: `${width}%` }}
              >
                {stage.displayValue || value.toLocaleString("en-US")}
              </div>
            </div>
            <strong>{stage.displayRate || `${Math.round(pct * 100)}%`}</strong>
          </div>
        );
      })}
    </div>
  );
}

export default function PdpDeepDiveTab({
  selectedProduct,
  onAnalyze = null,
  isAnalyzing = false,
  isCheckingAnalysis = false,
  analysisError = "",
  zeqiMemory = null,
  onRunTask = null,
  runningTaskId = null,
  taskStatusOverrides = {},
}) {
  const [activePdpTab, setActivePdpTab] = useState("overview");

  if (!selectedProduct) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateTitle}>
          Select a product from PDP Rankings to analyze
        </div>
        <div className={styles.emptyStateText}>
          Click any product row in the rankings tab to open its deep dive analysis.
        </div>
      </div>
    );
  }

  const digitalTwin = selectedProduct.digitalTwin || {};
  const tabs = Array.isArray(digitalTwin.tabs) ? digitalTwin.tabs : [];
  const sections = Array.isArray(digitalTwin.sections) ? digitalTwin.sections : [];
  const activeSection = useMemo(
    () => sections.find((section) => section.key === activePdpTab) || sections[0] || null,
    [activePdpTab, sections],
  );
  const hero = digitalTwin.hero || {};
  const badges = Array.isArray(digitalTwin.badges) ? digitalTwin.badges : [];
  const statusStrip = Array.isArray(digitalTwin.statusStrip) ? digitalTwin.statusStrip : [];
  const guardrails = Array.isArray(digitalTwin.guardrails) ? digitalTwin.guardrails : [];
  const workflow = digitalTwin.workflow || {};
  const attachedActions = Array.isArray(zeqiMemory?.actions)
    ? zeqiMemory.actions
    : Array.isArray(selectedProduct?.actions)
      ? selectedProduct.actions
      : [];
  const attachedTasks = Array.isArray(zeqiMemory?.tasks)
    ? zeqiMemory.tasks
    : Array.isArray(selectedProduct?.tasks)
      ? selectedProduct.tasks
      : [];
  const productPageUrl = resolveProductUrl(selectedProduct.url || hero.url);
  const getActionId = (action) => String(action?.id || action?._id || action?.actionId || "").trim();
  const getTaskId = (task) => String(task?.id || task?._id || task?.taskId || "").trim();
  const getTaskActionId = (task) => String(task?.actionId || task?.action_id || "").trim();
  const attachedActionIds = new Set(attachedActions.map(getActionId).filter(Boolean));
  const tasksByActionId = attachedTasks.reduce((map, task) => {
    const actionId = getTaskActionId(task);
    if (!actionId || !attachedActionIds.has(actionId)) return map;
    if (!map.has(actionId)) map.set(actionId, []);
    map.get(actionId).push(task);
    return map;
  }, new Map());
  const ungroupedTasks = attachedTasks.filter((task) => {
    const actionId = getTaskActionId(task);
    return !actionId || !attachedActionIds.has(actionId);
  });

  const renderTaskCard = (task, nested = false) => {
    const taskId = getTaskId(task);
    const rawStatus = taskStatusOverrides[taskId] || task.status || "Suggested";
    const isCompleted = String(rawStatus).toLowerCase() === "completed" || task.isCompleted;
    const status = isCompleted ? "Completed" : rawStatus;
    const isRunning = !isCompleted && (String(status).toLowerCase() === "running" || runningTaskId === taskId);
    return (
      <div
        key={`task-${taskId || task.title}`}
        className={`${styles.attachedWorkCard} ${nested ? styles.attachedWorkTaskNested : ""}`}
      >
        <div>
          <div className={styles.attachedWorkType}>Task</div>
          <div className={styles.attachedWorkTitle}>{task.title || "Untitled task"}</div>
          {task.shortDescription ? (
            <div className={styles.attachedWorkDescription}>{task.shortDescription}</div>
          ) : null}
          <div className={styles.attachedWorkMeta}>{status === "Suggested" ? "Pending" : status}</div>
        </div>
        {typeof onRunTask === "function" && taskId ? (
          <button
            type="button"
            className={styles.attachedWorkRunButton}
            onClick={() => onRunTask(task)}
            disabled={isRunning || isCompleted}
          >
            {isCompleted ? "Completed" : isRunning ? "Running..." : "Run This Task"}
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <div className={styles.pdpTwinShell}>
      <div className={styles.pdpTwinControlRow}>
        <div className={styles.pdpTwinControlLeft}>
          {digitalTwin.sourceSummary ? (
            <span className={styles.pdpTwinFilterPill}>{digitalTwin.sourceSummary}</span>
          ) : null}
        </div>
        <div className={styles.pdpTwinControlRight}>
          {badges.map((badge) => (
            <PdpBadge key={`${badge.label}-${badge.tone}`} tone={badge.tone}>{badge.label}</PdpBadge>
          ))}
        </div>
      </div>

      <section className={styles.pdpTwinHero}>
        <div className={styles.pdpTwinIdentity}>
          {hero.category || selectedProduct.category ? (
            <div className={styles.pdpTwinBreadcrumbs}>{hero.category || selectedProduct.category}</div>
          ) : null}
          <h2 className={styles.pdpTwinTitle}>{hero.title || selectedProduct.title || selectedProduct.itemName || "-"}</h2>
          {hero.url || selectedProduct.url ? (
            <div className={styles.pdpTwinUrlChip}>
              <strong>SKU URL</strong>
              <span>{hero.url || selectedProduct.url}</span>
            </div>
          ) : null}
          <div className={styles.pdpActionRow}>
            {productPageUrl ? (
              <button
                type="button"
                className={styles.pdpOpenButton}
                onClick={() => window.open(productPageUrl, "_blank", "noopener,noreferrer")}
              >
                Open product page
              </button>
            ) : null}
            {typeof onAnalyze === "function" ? (
              <button
                type="button"
                className={styles.pdpAnalyzeButton}
                onClick={onAnalyze}
                disabled={isAnalyzing || isCheckingAnalysis}
              >
                {isAnalyzing ? "Analyzing..." : isCheckingAnalysis ? "Checking..." : "Analyze URL"}
              </button>
            ) : null}
            {attachedTasks.length || attachedActions.length ? (
              <span className={styles.pdpTwinWorkHint}>Workflow ready</span>
            ) : null}
          </div>
          {analysisError ? <div className={styles.pdpAnalysisError}>{analysisError}</div> : null}
        </div>

        <aside className={styles.pdpTwinPriceCard}>
          <div className={styles.pdpTwinPriceTop}>
            <div>
              <div className={styles.pdpTwinScoreLabel}>SKU health</div>
              <div
                className={styles.pdpTwinScoreRing}
                style={{ "--pdp-score": `${Math.max(0, Math.min(100, Number(hero.healthScore || 0)))}%` }}
              >
                <span>{hero.healthScore ?? "-"}</span>
              </div>
            </div>
            <div>
              <div className={styles.pdpTwinPrice}>{hero.price || "-"}</div>
              <div className={styles.pdpTwinMargin}>{hero.margin || "margin N/A"}</div>
            </div>
          </div>
          {Array.isArray(hero.miniStats) && hero.miniStats.length ? (
            <div className={styles.pdpTwinMiniGrid}>
              {hero.miniStats.map((stat) => (
                <div key={stat.label} className={styles.pdpTwinMiniStat}>
                  <div className={statusClass(styles, stat.status)}>{stat.value ?? "-"}</div>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
          ) : null}
        </aside>
      </section>

      {statusStrip.length ? (
        <div className={styles.pdpTwinStatusStrip}>
          {statusStrip.map((cell) => (
            <div key={cell.label} className={styles.pdpTwinStatusCell}>
              <div>{cell.label}</div>
              <strong className={statusClass(styles, cell.status)}>{cell.value || "-"}</strong>
            </div>
          ))}
        </div>
      ) : null}

      {tabs.length ? (
        <nav className={styles.pdpTwinTabs} aria-label="PDP deep dive sections">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`${styles.pdpTwinTab} ${activeSection?.key === tab.key ? styles.pdpTwinTabActive : ""}`}
              onClick={() => setActivePdpTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      ) : null}

      {activeSection ? (
        <section className={styles.pdpTwinSectionGrid}>
          <div className={styles.pdpTwinPanel}>
            <div className={styles.pdpTwinPanelHeader}>
              <div>
                <h3>{activeSection.title}</h3>
                <p>{activeSection.subtitle}</p>
              </div>
              {activeSection.badge ? (
                <PdpBadge tone={activeSection.badge.tone}>{activeSection.badge.label}</PdpBadge>
              ) : null}
            </div>
            <div className={styles.pdpTwinPanelBody}>
              {Array.isArray(activeSection.metrics) && activeSection.metrics.length ? (
                <div className={styles.pdpTwinMetricGrid}>
                  {activeSection.metrics.map((metric) => (
                    <MetricCard
                      key={metric.label}
                      label={metric.label}
                      value={metric.value}
                      subtext={metric.subtext}
                      tone={metric.tone}
                    />
                  ))}
                </div>
              ) : null}
              {Array.isArray(activeSection.journeyStages) && activeSection.journeyStages.length ? (
                <PdpJourneyFunnel stages={activeSection.journeyStages} />
              ) : null}
              <PdpDataTable table={activeSection.table} />
            </div>
          </div>

          <aside className={styles.pdpTwinSideStack}>
            {Array.isArray(activeSection.decisionSummary) && activeSection.decisionSummary.length ? (
              <div className={styles.pdpTwinSideCard}>
                <h4>{activeSection.title} decision summary</h4>
                {activeSection.decisionSummary.map((row) => (
                  <div key={row.label} className={styles.pdpTwinDecisionRow}>
                    <span>{row.label}</span>
                    <strong>{row.value || "-"}</strong>
                  </div>
                ))}
              </div>
            ) : null}
            {guardrails.length ? (
              <div className={styles.pdpTwinSideCard}>
                <h4>Workflow guardrails</h4>
                {guardrails.map((row) => (
                  <div key={row.label} className={styles.pdpTwinDecisionRow}>
                    <span>{row.label}</span>
                    <strong>{row.value || "-"}</strong>
                  </div>
                ))}
              </div>
            ) : null}
          </aside>
        </section>
      ) : (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateTitle}>Product analysis not ready yet</div>
          <div className={styles.emptyStateText}>Analyze this product to see the PDP deep dive.</div>
        </div>
      )}

      <section className={styles.pdpTwinAgentPanel}>
        <div className={styles.pdpTwinAgentHeader}>
          <div className={styles.pdpTwinAgentTitle}>
            <div className={styles.pdpTwinAgentIcon}>AI</div>
            <div>
              <h3>{workflow.title || "Task Agentic AI"}</h3>
              <p>{workflow.description || ""}</p>
            </div>
          </div>
        </div>
        {attachedTasks.length || attachedActions.length ? (
          <div className={styles.attachedWorkList}>
            {attachedActions.map((action) => {
              const actionId = getActionId(action);
              const childTasks = actionId ? tasksByActionId.get(actionId) || [] : [];
              return (
                <div key={`action-${actionId || action.title}`} className={styles.attachedWorkActionGroup}>
                  <div className={styles.attachedWorkActionHeader}>
                    <div>
                      <div className={styles.attachedWorkType}>Action</div>
                      <div className={styles.attachedWorkTitle}>{action.title || "Untitled action"}</div>
                      <div className={styles.attachedWorkMeta}>{action.status || "Suggested"}</div>
                    </div>
                  </div>
                  {childTasks.length ? (
                    <div className={styles.attachedWorkNestedList}>
                      {childTasks.map((task) => renderTaskCard(task, true))}
                    </div>
                  ) : null}
                </div>
              );
            })}
            {ungroupedTasks.length ? (
              <div className={attachedActions.length ? styles.attachedWorkUngrouped : ""}>
                {attachedActions.length ? (
                  <div className={styles.attachedWorkUngroupedTitle}>Other tasks</div>
                ) : null}
                <div className={styles.attachedWorkNestedList}>
                  {ungroupedTasks.map((task) => renderTaskCard(task, Boolean(attachedActions.length)))}
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className={styles.pdpTwinEmptyWork}>
            <strong>{workflow.emptyTitle || "No workflow attached yet."}</strong>
            <span>{workflow.emptyDescription || ""}</span>
          </div>
        )}
      </section>
    </div>
  );
}
